import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card, Typography, Tag, Space, Divider, Empty, Spin, Button, Badge
} from "antd";
import {
  HomeOutlined, UsergroupAddOutlined, BookOutlined, CalendarOutlined,
  FileTextOutlined, VideoCameraOutlined, HandshakeOutlined,
  RiseOutlined, TrophyOutlined, CarryOutOutlined, FileProtectOutlined,
  DollarOutlined, AimOutlined, BankOutlined, DownloadOutlined,
  LinkOutlined, EnvironmentOutlined, PhoneOutlined, MailOutlined,
  Building2Outlined, PhoneFilled, MailFilled, GlobalOutlined,
  RocketOutlined,
  ArrowRightOutlined,
  FilePdfOutlined,
  LineChartOutlined,
  NodeIndexOutlined,
  TeamOutlined,
  PieChartOutlined,
  LikeOutlined
} from "@ant-design/icons";
import {
  Phone, Mail, Link as LinkIcon, ChevronLeft, ChevronRight,
  Building2, AlertCircle,
  Share2,
  Download,
  Info,
  Menu,
  X
} from "lucide-react";

const { Title, Paragraph, Text } = Typography;

const IndustryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [industry, setIndustry] = useState(null);
  console.log("Industry Data:", industry);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Add new state for companies
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState(null);

  useEffect(() => {
    const fetchIndustry = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/industries/${id}`);
        const data = await response.json();

        if (data.success) {
          setIndustry(data.data);
        } else {
          setError('Failed to load industry information');
        }
      } catch (err) {
        setError('Error loading industry information');
        console.error('Error fetching industry:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchIndustry();
    }
  }, [id]);

  // Fetch companies when industry data is loaded
  useEffect(() => {
    const fetchIndustryCompanies = async () => {
      if (!industry?.sectorName) return;

      try {
        setCompaniesLoading(true);
        setCompaniesError(null);

        // Fetch companies filtered by industry sector name
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/companies/bycity?industry=${encodeURIComponent(industry.sectorName)}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          setCompanies(result.data || []);
        } else {
          throw new Error(result.message || 'Failed to fetch companies');
        }
      } catch (err) {
        setCompaniesError(err.message);
        console.error('Error fetching companies:', err);
      } finally {
        setCompaniesLoading(false);
      }
    };

    fetchIndustryCompanies();
  }, [industry?.sectorName]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "80px 0" }}>
        <Spin size="large" />
        <p style={{ marginTop: "20px", fontFamily: "Rubik, sans-serif", fontSize: "18px", color: "#470172" }}>
          Loading industry information...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "80px 0" }}>
        <Empty description={
          <span style={{ fontFamily: "Rubik, sans-serif", fontSize: "18px", fontWeight: 400, color: "#470172" }}>
            {error}
          </span>
        } />
      </div>
    );
  }

  if (!industry) {
    return null;
  }

  const tabs = [
    { id: "overview", label: "Overview", icon: <HomeOutlined /> },
    { id: "chair", label: "Conveners/Co-Conveners", icon: <UsergroupAddOutlined /> },
    { id: "contribution", label: "Vidarbha Contribution", icon: <RiseOutlined /> },
    { id: "highlights", label: "Sector Highlights", icon: <FileProtectOutlined /> },
    { id: "players", label: "Key Players", icon: <BankOutlined /> },
    { id: "developments", label: "Key Developments", icon: <CarryOutOutlined /> },
    { id: "policies", label: "Policies", icon: <FileTextOutlined /> },
    { id: "incentives", label: "Incentives", icon: <DollarOutlined /> },
    { id: "opportunities", label: "Opportunities", icon: <AimOutlined /> },
    { id: "media", label: "Media Coverage", icon: <VideoCameraOutlined /> },
    { id: "press", label: "Press Releases", icon: <FileTextOutlined /> },
    { id: "blogs", label: "Blogs", icon: <BookOutlined /> },
    { id: "events", label: "Events", icon: <CalendarOutlined /> },
    { id: "awards", label: "Awards", icon: <TrophyOutlined /> },
    { id: "resources", label: "Resources", icon: <DownloadOutlined /> },
    { id: "surveys", label: "Surveys", icon: <FileProtectOutlined /> },
    { id: "success", label: "Success Stories", icon: <RiseOutlined /> },
  ];

  const renderTabContent = () => {
    const baseTextStyle = {
      fontFamily: "Rubik, sans-serif",
      fontSize: "17px",
      fontWeight: 400,
      color: "#666666"
    };
    const headingStyle = {
      fontFamily: "Rubik, sans-serif",
      fontWeight: 400,
      color: "#470172",
      marginBottom: "24px",
      fontSize: "24px",
      // borderBottom: "2px solid #470172",
      display: "inline-block",
      paddingBottom: "3px", // spacing between text and line
    };

    // Banner component to be used in all tabs
    const BannerImage = () => (
      <div style={{
        width: "100%",
        height: "320px",
        marginBottom: "30px",
        overflow: "hidden",
        borderRadius: "4px"
      }}>
        <img
          src={industry?.bannerImage ? `${process.env.REACT_APP_API_BASE_URL}${industry.bannerImage}` : "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=320&fit=crop"}
          alt={industry?.sectorName || "Industry"}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover"
          }}
        />
      </div>
    );



    switch (activeTab) {
      case "overview":
        return (
          <div>
            <div className="industry-banner-wrap" style={{ flex: 1, padding: "0 40px" }}>
              <div>

                <div style={{
                  width: "100%",
                  height: "320px",
                  marginBottom: "30px",
                  overflow: "hidden",
                  borderRadius: "4px"
                }}>
                  <img
                    src={industry?.bannerImage ? `${process.env.REACT_APP_API_BASE_URL}${industry.bannerImage}` : "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=320&fit=crop"}
                    alt={industry?.sectorName || "Industry"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover"
                    }}
                  />
                </div>
              </div>

            </div>
            <Title level={2} style={{ ...headingStyle }}>
              Vidarbha Overview
            </Title>
            <Paragraph style={{ ...baseTextStyle, lineHeight: "1.8", marginBottom: "30px" }}>
              {industry.regional_overview || "No overview available"}
            </Paragraph>

            {industry.regional_highlights && industry.regional_highlights.length > 0 && (
              <div style={{ padding: "40px 0", maxWidth: "1200px", margin: "0 auto", fontFamily: "'Rubik', sans-serif" }}>


                {/* Reduced Width Grid: minmax lowered to 280px for narrower, more elegant cards */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
                  gap: "24px",
                  padding: "0 20px"
                }}>
                  {industry.regional_highlights.map((highlight, index) => (
                    <div
                      key={index}
                      style={{
                        // background: "#ffffff",
                        background: "linear-gradient(135deg, rgba(255, 143, 0, 0.03) 0%, rgba(255, 143, 0, 0.06) 100%)",
                        borderRadius: "12px",
                        padding: "40px 20px 28px 20px",
                        border: "1px solid #F3F4F6",
                        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.04)",
                        transition: "all 0.3s ease",
                        position: "relative",
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-6px)";
                        e.currentTarget.style.boxShadow = "0 16px 32px rgba(60, 0, 122, 0.08)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.04)";
                      }}
                    >
                      {/* Floating Icon Container */}
                      <div style={{
                        position: "absolute",
                        top: "-30px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        background: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #E5E7EB",
                        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.08)",
                        zIndex: 2
                      }}>
                        <div style={{ fontSize: "28px", color: "#333", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {index % 4 === 0 && <BankOutlined />}
                          {index % 4 === 1 && <LineChartOutlined />}
                          {index % 4 === 2 && <NodeIndexOutlined />}
                          {index % 4 === 3 && <EnvironmentOutlined />}
                        </div>
                      </div>

                      {/* Accent Header Line */}
                      {/* <div style={{
                        width: '100%',
                        height: '3px',
                        background: '#FF8F00',
                        borderRadius: '12px 12px 0 0',
                        position: 'absolute',
                        top: 0,
                        left: 0
                      }} /> */}

                      {/* Red Highlight Title */}
                      <Title level={4} style={{
                        color: "#FF8F00",
                        fontSize: "18px",
                        fontWeight: 400,
                        margin: "0 0 10px 0",
                        fontFamily: "'Rubik', sans-serif"
                      }}>
                        {highlight?.title}
                      </Title>

                      {/* Description Text */}
                      <Paragraph style={{
                        color: "#666666",
                        fontSize: "14px",
                        lineHeight: "1.6",
                        fontWeight: 400,
                        margin: 0,
                        fontFamily: "'Rubik', sans-serif"
                      }}>
                        {highlight?.description}
                      </Paragraph>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );


      case "chair":
        return (
          <div style={{ padding: "20px 0" }}>
            <BannerImage />
            <Title level={2} style={{ ...headingStyle, marginBottom: "32px" }}>
              Conveners & Co-Conveners
            </Title>

            {Array.isArray(industry.chairCoChairMembers) && industry.chairCoChairMembers.length > 0 ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
                  gap: 24,
                }}
              >
                {industry.chairCoChairMembers.map((p, i) => {
                  const photoSrc = p.photo ? `${process.env.REACT_APP_API_BASE_URL}/uploads/${p.photo}` : null;
                  const initials = (p.name || "Member").trim().split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
                  const isChair = p.role?.toLowerCase() === "chair" || p.role?.toLowerCase() === "convener";
                  const displayRole = (role) => {
                    if (!role) return "Member";
                    const lower = role.toLowerCase();
                    if (lower === "chair") return "Convener";
                    if (lower === "cochair" || lower === "co-chair") return "Co-Convener";
                    return role;
                  };

                  return (
                    <div
                      key={i}
                      style={{
                        background: "#ffffff",
                        borderRadius: 20,
                        padding: "24px",
                        border: "1px solid #f1f1f1",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
                        transition: "all 0.3s ease",
                        position: "relative",
                        overflow: "hidden",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-8px)";
                        e.currentTarget.style.borderColor = "#3C007A";
                        e.currentTarget.style.boxShadow = "0 20px 40px rgba(60, 0, 122, 0.08)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.borderColor = "#f1f1f1";
                        e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.03)";
                      }}
                    >
                      {/* Visual Role Indicator Tab */}
                      <div
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 16,
                          padding: "4px 12px",
                          background: isChair ? "#3C007A" : "#FF8F00",
                          color: "#fff",
                          fontSize: "13px",
                          fontWeight: 400,
                          // textTransform: "uppercase",
                          letterSpacing: "1px",
                          borderRadius: "5px",
                        }}
                      >
                        {displayRole(p.role)}
                      </div>

                      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
                        {/* High-Quality Avatar */}
                        <div
                          style={{
                            width: 100,
                            height: 100,
                            borderRadius: 16,
                            background: "#F3F4F6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "3px solid #fff",
                            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}
                        >
                          {photoSrc ? (
                            <img src={photoSrc} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            <div style={{ color: "#3C007A", fontSize: "32px", fontWeight: 600 }}>{initials}</div>
                          )}
                        </div>

                        <div style={{ flex: 1, paddingTop: 32 }}>
                          <div style={{ color: "#333333", fontWeight: 500, fontSize: "20px", marginBottom: "4px" }}>
                            {p.fullName || "Member Name"}
                          </div>
                          <div style={{ color: "#FF8F00", fontWeight: 400, fontSize: "14px", letterSpacing: "0.5px" }}>
                            {p.designation || "Position Not Specified"}
                          </div>
                        </div>
                      </div>

                      {/* Divider Line */}
                      <div style={{ height: "1px", background: "linear-gradient(to right, #f0f0f0, #ffffff)", margin: "16px 0" }} />

                      {/* Bio Section */}
                      {p.description && (
                        <p
                          style={{
                            color: "#666",
                            fontSize: "15px",
                            lineHeight: "1.7",
                            fontWeight: 400,
                            margin: 0,
                          }}
                        >
                          {p.description.length > 180 ? `${p.description.slice(0, 180)}…` : p.description}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                style={{
                  padding: "60px 0",
                  textAlign: "center",
                  background: "#F9FAFB",
                  borderRadius: 16,
                  border: "2px dashed #E5E7EB",
                }}
              >
                <Text style={{ ...baseTextStyle, color: "#999" }}>No leadership members added.</Text>
              </div>
            )}
          </div>
        );

      case "contribution":
        return (
          <div>
            <BannerImage />
            <Title level={2} style={{ ...headingStyle }}>
              Vidarbha Regional Contribution
            </Title>
            <Paragraph style={{ ...baseTextStyle, lineHeight: "1.8", marginBottom: "30px" }}>
              {industry.vidarbhaContribution?.overview || "No contribution overview available"}
            </Paragraph>

            {industry.vidarbhaContribution?.statistics && industry.vidarbhaContribution.statistics.length > 0 && (
              <>
                <Title level={3} style={{ ...headingStyle, marginTop: "20px" }}>
                  Key Statistics
                </Title>
                <div style={{ padding: "10px 0", fontFamily: "'Rubik', sans-serif" }}>
                  {/* Section Header with Accent */}

                  {industry.vidarbhaContribution?.statistics && industry.vidarbhaContribution.statistics.length > 0 ? (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
                      gap: "24px"
                    }}>
                      {industry.vidarbhaContribution.statistics.map((stat, index) => (
                        <div
                          key={index}
                          style={{
                            background: "linear-gradient(135deg, rgba(255, 143, 0, 0.03) 0%, rgba(255, 143, 0, 0.06) 100%)",
                            borderRadius: "12px",
                            padding: "20px 12px",
                            textAlign: "center",
                            border: "1px solid #E5E7EB",
                            boxShadow: "0 10px 25px rgba(0,0,0,0.03)",
                            transition: "all 0.3s ease",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "16px"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-5px)";
                            e.currentTarget.style.boxShadow = "0 15px 30px rgba(0,0,0,0.08)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 10px 25px rgba(0,0,0,0.03)";
                          }}
                        >
                          {/* Professional Teal Icons following screenshot */}
                          {/* <div style={{ color: "#00A99D", fontSize: "48px", marginBottom: "8px" }}>
                            {index === 0 && <TeamOutlined />}
                            {index === 1 && <GlobalOutlined />}
                            {index === 2 && <PieChartOutlined />}
                            {index === 3 && <LikeOutlined />}
                            {index === 4 && <FileTextOutlined />}
                            {index === 5 && <RiseOutlined />}
                          </div> */}

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <Text style={{
                              color: "#3C007A",
                              fontSize: "21px",
                              fontWeight: 500, // Explicitly 400 as requested
                              display: "block",
                              fontFamily: "'Rubik', sans-serif"
                            }}>
                              {stat.value}
                            </Text>

                            <Text style={{
                              color: "#666666",
                              fontSize: "17px",
                              fontWeight: 400,
                              fontFamily: "'Rubik', sans-serif"
                            }}>
                              {stat.title}
                            </Text>
                          </div>

                          {stat.description && (
                            <Paragraph style={{
                              color: "#666666",
                              fontSize: "14px",
                              lineHeight: "1.5",
                              fontWeight: 400,
                              margin: 0,
                              maxWidth: "200px"
                            }}>
                              {stat.description}
                            </Paragraph>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: "60px", textAlign: "center", background: "#f9f9f9", borderRadius: "12px" }}>
                      <Empty description="No statistics available" />
                    </div>
                  )}
                </div>
              </>
            )}

          </div>
        );

      case "highlights":
        return (
          <div style={{ padding: "40px 0", fontFamily: "'Inter', sans-serif" }}>
            {/* Professional Section Header */}
            <div style={{ marginBottom: '50px', paddingBottom: '16px' }}>
              <Title level={2} style={{
                margin: 0,
                color: "#3C007A",
                fontSize: "24px",
                fontWeight: 400,
                letterSpacing: "-0.5px"
              }}>
                Sector Highlights Documents
              </Title>
            </div>

            {industry.highlight_docs && industry.highlight_docs.length > 0 ? (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))",
                gap: "12px"
              }}>
                {industry.highlight_docs.map((doc, index) => (
                  <div
                    key={index}
                    style={{
                      background: "#ffffff",
                      borderRadius: "12px",
                      padding: "24px",
                      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
                      display: "flex",
                      alignItems: "center",
                      gap: "20px",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      // border: "1px solid #F0F0F0",
                      position: "relative",
                      overflow: "hidden"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-5px)";
                      e.currentTarget.style.boxShadow = "0 15px 35px rgba(60, 0, 122, 0.08)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 10px 30px rgba(0, 0, 0, 0.05)";
                    }}
                  >
                    {/* PDF Document Icon Container */}
                    <div style={{ flexShrink: 0 }}>

                      {/* Abstract Folder/PDF Icon matching image_4d16fa.png */}
                      <FilePdfOutlined
                        style={{
                          fontSize: 30,
                          color: "#333",
                          background: "rgba(255,77,79,0.08)",
                          padding: 6,
                          borderRadius: 6
                        }}
                      />


                    </div>

                    {/* Text Content Area */}
                    <div style={{ flexGrow: 1 }}>
                      <Text style={{
                        display: "block",
                        color: "#FF8F00",
                        fontSize: "15px",
                        fontWeight: 400,
                        // textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "4px"
                      }}>
                        Click here to view
                      </Text>
                      <Title level={4} style={{
                        margin: 0,
                        color: "#666666",
                        fontSize: "16px",
                        fontWeight: 400,
                        lineHeight: 1.3
                      }}>
                        {doc.title}
                      </Title>
                    </div>

                    {/* Download Action Button */}
                    <div style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      background: "#3C007A", // Signature purple from screenshot
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 4px 10px rgba(156, 39, 176, 0.3)",
                      flexShrink: 0
                    }}>
                      <DownloadOutlined style={{ fontSize: "20px", color: "#fff" }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: "80px 0", textAlign: "center", background: "#F9FAFB", borderRadius: "12px" }}>
                <Empty description={<Text style={{ color: "#8C8C8C", fontSize: "16px" }}>No highlight documents available at this time.</Text>} />
              </div>
            )}
          </div>
        );

      case "players":
        return (
          <div>
            <BannerImage />
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "50px"
            }}>
              <Title level={2} style={{ ...headingStyle, marginBottom: 0 }}>
                Key Industry Players
              </Title>

              {industry.key_players && industry.key_players.length > 0 && (
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => {
                      const container = document.getElementById('key-players-container');
                      if (container) {
                        container.scrollBy({ left: -300, behavior: 'smooth' });
                      }
                    }}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      border: "2px solid #E5E7EB",
                      background: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#470172";
                      e.currentTarget.style.background = "#F3E8FF";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#E5E7EB";
                      e.currentTarget.style.background = "#fff";
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#470172" strokeWidth="2">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </button>

                  <button
                    onClick={() => {
                      const container = document.getElementById('key-players-container');
                      if (container) {
                        container.scrollBy({ left: 300, behavior: 'smooth' });
                      }
                    }}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      border: "2px solid #E5E7EB",
                      background: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#470172";
                      e.currentTarget.style.background = "#F3E8FF";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#E5E7EB";
                      e.currentTarget.style.background = "#fff";
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#470172" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>
              )}
            </div>

            {industry.key_players && industry.key_players.length > 0 ? (
              <div
                id="key-players-container"
                style={{
                  display: "flex",
                  gap: "24px",
                  overflowX: "auto",
                  overflowY: "hidden",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  WebkitOverflowScrolling: "touch",
                  paddingBottom: "20px"
                }}
              >
                <style>{`
                  #key-players-container::-webkit-scrollbar {
                    display: none;
                  }
                `}</style>

                {industry.key_players.map((player, index) => (
                  <div
                    key={index}
                    style={{
                      minWidth: "200px",
                      maxWidth: "200px",
                      flexShrink: 0,
                      background: "#ffffff",
                      borderRadius: "16px",
                      padding: "32px 24px",
                      border: "1px solid #f1f1f1",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      textAlign: "center",
                      transition: "all 0.3s ease",
                      cursor: "pointer"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-8px)";
                      e.currentTarget.style.boxShadow = "0 12px 24px rgba(71, 1, 114, 0.12)";
                      e.currentTarget.style.borderColor = "#470172";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.05)";
                      e.currentTarget.style.borderColor = "#f1f1f1";
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
                      <img src="/img/corporate.png" alt="Corporate" style={{ width: "48px", height: "48px" }} />
                    </div>
                    <Title level={4} style={{ ...headingStyle, fontSize: "16px", margin: 0 }}>
                      {player.companyName}
                    </Title>
                  </div>
                ))}
              </div>
            ) : (
              <Empty description={<Text style={baseTextStyle}>No key players information available</Text>} />
            )}
          </div>
        );

      case "developments":
        return (
          <div style={{ padding: "10px 0", fontFamily: "'Inter', sans-serif", background: "#fdf8f5" }}>
            {/* Section Header with Orange Accent */}
            <div style={{
              textAlign: 'center',
              marginBottom: '60px',
              background: 'linear-gradient(90deg, #F37335 0%, #FDC830 100%)',
              padding: '12px',
              borderRadius: '4px'
            }}>
              <Title level={2} style={{ margin: 0, color: "#3C007A", fontSize: "21px", fontWeight: 500, textTransform: 'uppercase' }}>
                Key Development in the Industry
              </Title>
            </div>

            {industry.developments && industry.developments.length > 0 ? (
              <div style={{ position: 'relative', maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
                {/* Main vertical connecting line */}
                <div style={{
                  position: 'absolute',
                  left: '50%',
                  top: '0',
                  bottom: '0',
                  width: '2px',
                  background: 'linear-gradient(to bottom, #E879F9, #4ADE80, #F87171, #A78BFA)',
                  transform: 'translateX(-50%)',
                  zIndex: 1
                }} />

                {industry.developments.map((dev, index) => {
                  const isEven = index % 2 === 0;
                  const themeColors = ["#E879F9", "#60A5FA", "#F87171", "#A78BFA"];
                  const currentColor = themeColors[index % themeColors.length];

                  return (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: isEven ? 'flex-end' : 'flex-start',
                      alignItems: 'center',
                      marginBottom: '40px',
                      position: 'relative',
                      zIndex: 2
                    }}>
                      {/* Content Card */}
                      <div style={{
                        width: 'calc(50% - 60px)',
                        background: '#fff',
                        padding: '24px 32px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                        position: 'relative'
                      }}>
                        <p style={{
                          margin: 0,
                          fontSize: '15px',
                          color: '#333',
                          lineHeight: '1.6',
                          fontWeight: 400
                        }}>
                          {dev.description}
                        </p>
                      </div>

                      {/* Circular Icon Marker */}
                      <div style={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: '#fff',
                        border: `2px solid ${currentColor}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                        overflow: 'hidden'
                      }}>
                        {dev.icon ? (
                          <img
                            src={`${process.env.REACT_APP_API_BASE_URL}${dev.icon}`}
                            alt="Development icon"
                            style={{ width: '36px', height: '36px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{ color: currentColor, fontSize: '24px' }}>
                            {index === 0 && <span style={{ fontSize: '20px' }}>📊</span>}
                            {index === 1 && <span style={{ fontSize: '20px' }}>🌳</span>}
                            {index === 2 && <span style={{ fontSize: '20px' }}>🎓</span>}
                            {index === 3 && <span style={{ fontSize: '20px' }}>🏢</span>}
                            {index > 3 && <span style={{ fontSize: '20px' }}>✨</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '12px' }}>
                <Text style={{ color: '#999' }}>No developments information available</Text>
              </div>
            )}
          </div>
        );

      case "policies":
        return (
          <div>
            <BannerImage />
            <Title level={2} style={{ ...headingStyle }}>
              Policies
            </Title>

            {/* <div style={{
              height: "1px",
              background: "linear-gradient(90deg, transparent 0%, #0c101aff 50%, transparent 100%)",
              marginBottom: "20px"
            }} /> */}
            {/* {industry.policies && industry.policies.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" , marginTop:"50px"}}>
                {industry.policies.map((policy, index) => (
                  <div key={index} style={{
                    padding: "25px",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "15px" }}>
                      <h3 style={{ fontSize: "20px", fontWeight: "500", color: "#470172" }}>
                        {policy.policyName}
                      </h3>
                      <span style={{ fontSize: "14px", color: "#666" }}>
                        {policy.date ? new Date(policy.date).toLocaleDateString() : ""}
                      </span>
                    </div>
                    <p style={{ fontSize: "15px", color: "#555", lineHeight: "1.6" }}>
                      {policy.description}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: "#999" }}>No policies information available</p>
            )} */}

            {Array.isArray(industry?.policies) && industry?.policies.length ? (
              <div
                // ref={policiesContainerRef}
                style={{
                  display: "flex",
                  gap: 20,
                  overflowX: "auto",
                  padding: "10px 5px",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  WebkitOverflowScrolling: "touch"
                }}
              >
                {industry?.policies.map((p, i) => (
                  <div
                    key={i}
                    style={{
                      minWidth: 320,
                      maxWidth: 320,
                      padding: "30px 24px",
                      background: "linear-gradient(135deg, rgba(60, 0, 122, 0.05) 0%, rgba(60, 0, 122, 0.08) 100%)",
                      borderRadius: 16,
                      position: "relative",
                      boxShadow: "0 4px 15px rgba(60, 0, 122, 0.1)",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      border: "1px solid rgba(60, 0, 122, 0.15)",
                      fontFamily: "'Rubik', sans-serif"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-6px)";
                      e.currentTarget.style.boxShadow = "0 12px 25px rgba(60, 0, 122, 0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 15px rgba(60, 0, 122, 0.1)";
                    }}
                  >
                    {/* Top-right Download Tab */}
                    <div style={{
                      position: "absolute",
                      top: 0,
                      right: 10,
                      width: 48,
                      height: 60,
                      background: "#fff",
                      borderRadius: "0 0 12px 12px",
                      boxShadow: "0 4px 10px rgba(60, 0, 122, 0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <Download size={22} color="#3C007A" strokeWidth={2.5} />
                    </div>

                    {/* Policy Graphic Icon Container */}


                    {/* Text Content */}
                    <h5 style={{
                      margin: "0 0 12px 0",
                      color: "#3C007A",
                      fontSize: "18px",
                      fontWeight: 400,
                      lineHeight: 1.3,
                      marginTop: "5px",
                      fontFamily: "'Rubik', sans-serif",
                      marginRight: "30px"
                    }}>
                      {p.policyName || "Policy"}
                    </h5>

                    <p style={{
                      margin: 0,
                      color: "#666666",
                      fontSize: "16px",
                      fontWeight: 400,
                      lineHeight: 1.6,
                      fontFamily: "'Rubik', sans-serif"
                    }}>
                      {p.description || "Policy details and information"}
                    </p>

                    {p.date && (
                      <>
                        <Divider style={{ margin: "20px 0 16px 0", borderColor: "rgba(60, 0, 122, 0.15)" }} />
                        <div style={{
                          fontSize: "16px",
                          color: "#333333",
                          fontWeight: 400,
                          fontFamily: "'Rubik', sans-serif",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px"
                        }}>
                          <CalendarOutlined style={{ color: "#3C007A", fontSize: "16px" }} />
                          {p.date}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                padding: "40px 0",
                textAlign: "center",
                color: "#666666",
                fontFamily: "'Rubik', sans-serif",
                fontSize: "17px",
                fontWeight: 400
              }}>
                No policies available.
              </div>
            )}
          </div>
        );

      case "incentives":
        const getThemeColor = (index) => {
          const palette = ["#9C27B0"];
          return palette[index % palette.length];
        };

        return (
          <div style={{ padding: "40px 0", fontFamily: "'Inter', sans-serif" }}>
            <BannerImage />
            {/* Professional Section Header */}
            <div style={{ marginBottom: '48px', textAlign: 'center' }}>
              <Title level={2} style={{
                color: "#3C007A",
                fontSize: "24px",
                fontWeight: 400,
                letterSpacing: "-0.5px"
              }}>
                Incentives
              </Title>

            </div>

            {Array.isArray(industry?.incentives) && industry.incentives.length > 0 ? (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: "32px",
                alignItems: "start"
              }}>
                {industry.incentives.map((incentive, index) => {
                  const themeColor = getThemeColor(index);

                  return (
                    <div key={index} style={{ display: 'flex', flexDirection: 'column' }}>
                      {/* Enterprise Header Bar */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        paddingBottom: '10px',
                        borderBottom: `3px solid ${themeColor}`,
                        marginBottom: '20px',
                        position: 'relative'
                      }}>
                        <Text style={{
                          color: themeColor,
                          fontWeight: 500,
                          fontSize: '17px',
                          textTransform: 'capitalize'
                        }}>
                          {incentive.title || "Incentive Measure"}
                        </Text>



                        {/* Bottom Triangle Accent */}
                        <div style={{
                          position: 'absolute',
                          bottom: '-12px',
                          left: '0',
                          width: '0',
                          height: '0',
                          borderLeft: '10px solid transparent',
                          borderRight: '10px solid transparent',
                          borderTop: `10px solid ${themeColor}`
                        }} />
                      </div>

                      {/* Content Card */}
                      <div style={{
                        background: "linear-gradient(135deg, rgba(255, 143, 0, 0.03) 0%, rgba(255, 143, 0, 0.06) 100%)",
                        borderRadius: "12px",
                        padding: "28px",
                        border: "1px solid rgba(255, 143, 0, 0.15)",
                        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05)",
                        minHeight: "280px",
                        transition: "all 0.3s ease",
                        cursor: "pointer"
                      }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-8px)";
                          e.currentTarget.style.boxShadow = "0 20px 40px rgba(255, 143, 0, 0.15)";
                          e.currentTarget.style.borderColor = "#FF8F00";
                          e.currentTarget.style.background = "linear-gradient(135deg, rgba(255, 143, 0, 0.08) 0%, rgba(255, 143, 0, 0.12) 100%)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 10px 25px -5px rgba(0, 0, 0, 0.05)";
                          e.currentTarget.style.borderColor = "rgba(255, 143, 0, 0.15)";
                          e.currentTarget.style.background = "linear-gradient(135deg, rgba(255, 143, 0, 0.03) 0%, rgba(255, 143, 0, 0.06) 100%)";
                        }}>

                        <div style={{
                          color: "#666666",
                          fontSize: "16px",
                          lineHeight: "1.7",
                          fontWeight: 300,
                          textAlign: "left"
                        }}>
                          {incentive.details.split('\n').map((line, i) => (
                            <div key={i} style={{
                              display: 'flex',
                              gap: '12px',
                              marginBottom: '12px',
                              alignItems: 'flex-start',
                              textAlign: 'left'
                            }}>
                              <span style={{
                                color: '#DC2626',
                                fontSize: '12px',
                                marginTop: '2px',
                                flexShrink: 0
                              }}>
                                ■
                              </span>
                              <span>{line.replace(/^[•\s*-]+/, '')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                padding: "80px",
                textAlign: "center",
                background: "#F9FAFB",
                borderRadius: "16px",
                border: "2px dashed #E5E7EB"
              }}>
                <Empty description={<Text style={{ color: "#6B7280" }}>No incentive schemes found for this sector.</Text>} />
              </div>
            )}
          </div>
        );
      case "opportunities":
        const getHeaderStyle = (index) => {
          const colors = ["#3C007A"];
          return colors[index % colors.length];
        };

        return (
          <div style={{ padding: "40px 0", fontFamily: "'Inter', sans-serif" }}>
            <BannerImage />
            {/* Section Header */}
            <div style={{ marginBottom: '60px', textAlign: 'center' }}>
              <Title level={2} style={{ color: "#3C007A", fontSize: "24px", fontWeight: 400 }}>
                Opportunities
              </Title>
            </div>

            {industry.opportunities && industry.opportunities.length > 0 ? (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "24px",
                alignItems: "start"
              }}>
                {industry.opportunities.map((opp, index) => {
                  const themeColor = getHeaderStyle(index);
                  return (
                    <div key={index} style={{ display: 'flex', flexDirection: 'column' }}>
                      {/* Visual Header with Numbered Marker */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        paddingBottom: '8px',
                        borderBottom: `3px solid ${themeColor}`,
                        marginBottom: '16px',
                        position: 'relative'
                      }}>
                        <Text style={{
                          color: themeColor,
                          fontWeight: 400,
                          fontSize: '18px'
                        }}>
                          {opp?.type || "Opportunity"}
                        </Text>

                        {/* Arrow Marker with Number */}
                        {/* <div style={{
                          position: 'relative',
                          width: '35px',
                          height: '24px',
                          background: themeColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 50% 70%, 0% 100%)',
                          transform: 'translateY(8px)'
                        }}>
                          <Text style={{ color: '#fff', fontSize: '12px', fontWeight: 'semibold', marginBottom: '4px' }}>
                            {index + 1}
                          </Text>
                        </div> */}

                        {/* Bottom Triangle Notch */}
                        <div style={{
                          position: 'absolute',
                          bottom: '-11px',
                          left: '0',
                          width: '0',
                          height: '0',
                          borderLeft: '8px solid transparent',
                          borderRight: '8px solid transparent',
                          borderTop: `8px solid ${themeColor}`
                        }} />
                      </div>

                      {/* Content Card */}
                      <div style={{
                        background: "linear-gradient(135deg, rgba(255, 143, 0, 0.03) 0%, rgba(255, 143, 0, 0.06) 100%)",
                        borderRadius: "12px",
                        padding: "28px",
                        border: "1px solid rgba(255, 143, 0, 0.15)",
                        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05)",
                        minHeight: "100px",
                        transition: "all 0.3s ease",
                        cursor: "pointer"
                      }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-8px)";
                          e.currentTarget.style.boxShadow = "0 12px 30px rgba(0,0,0,0.15)";
                          e.currentTarget.style.borderColor = themeColor;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.05)";
                          e.currentTarget.style.borderColor = "#E0E4E8";
                        }}>
                        <Title level={5} style={{
                          color: "#333",
                          fontSize: "16px",
                          fontWeight: 400,
                          marginBottom: "16px"
                        }}>
                          {opp?.title}
                        </Title>

                        <ul style={{
                          paddingLeft: "18px",
                          margin: 0,
                          color: "#555",
                          fontSize: "14px",
                          lineHeight: "1.6"
                        }}>
                          {/* Checking if description is an array or string to handle bullets */}
                          {Array.isArray(opp?.details) ? opp?.details?.map((detail, i) => (
                            <li key={i} style={{ marginBottom: '8px', paddingLeft: '4px' }}>
                              {/* <span style={{ color: '#D32F2F', marginRight: '8px' }}>■</span> */}
                              {detail}
                            </li>
                          )) : (
                            <li style={{ listStyleType: 'none', position: 'relative', marginLeft: '-10px' }}>
                              {/* <span style={{ color: '#D32F2F', marginRight: '8px' }}>■</span> */}
                              {opp?.description}
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: "60px", textAlign: "center", background: "#f9f9f9", borderRadius: "12px" }}>
                <Empty description="No measures available" />
              </div>
            )}
          </div>
        );
      case "media":
        return (
          <div>
            <BannerImage />
            <Title level={2} style={{ ...headingStyle }}>
              Media Coverage
            </Title>
            {Array.isArray(industry?.media_coverage) && industry?.media_coverage.length ? (
              <div style={{
                display: "grid",
                // Reduced card width by increasing column count and min-width adjustment
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 28
              }}>
                {industry?.media_coverage.map((m, i) => (
                  <div
                    key={i}
                    style={{
                      background: "#ffffff",
                      borderRadius: 20,
                      padding: 12,
                      display: "flex",
                      flexDirection: "column",
                      border: "1px solid #f1f1f1",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.02)"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-6px)";
                      e.currentTarget.style.boxShadow = "0 12px 24px rgba(60, 0, 122, 0.08)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.02)";
                    }}
                  >
                    {/* Refined Image Container with 'cover' object-fit */}
                    <div style={{
                      background: "#f9fafb",
                      borderRadius: 16,
                      height: 160,
                      overflow: "hidden",
                      marginBottom: 16,
                      border: "1px solid #f8f8f8"
                    }}>
                      <img
                        src={m.image ? `${process.env.REACT_APP_API_BASE_URL}/uploads/${m.image}` : "/api/placeholder/400/220"}
                        alt={m.title || "News Preview"}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    </div>

                    {/* News Title Section */}
                    <div style={{ padding: "0 8px" }}>
                      <h5 style={{
                        fontSize: "16px",
                        color: "#333333",
                        fontWeight: 400,
                        lineHeight: 1.5,
                        margin: "0 0 16px 0",
                        height: "72px", // Consistent height for 3 lines
                        display: "-webkit-box",
                        WebkitLineClamp: "3",
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        fontFamily: "'Rubik', sans-serif"
                      }}>
                        {m.title || "Media Coverage Summary"}
                      </h5>

                      {/* Info Bar */}
                      {/* Footer Section */}
                      <div style={{
                        marginTop: "auto",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingTop: "12px",
                        borderTop: "1px solid #f0f0f0"
                      }}>
                        {/* Side-by-Side Source and Date */}
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{
                            color: "#3C007A",
                            fontSize: "14px",
                            fontWeight: 400,
                            fontFamily: "'Rubik', sans-serif",
                            background: "rgba(60, 0, 122, 0.08)",
                            padding: "4px 10px",
                            borderRadius: "6px"
                          }}>
                            {m.sourceName || "Times of India"}
                          </span>

                          {/* Visual Separator */}
                          <span style={{ color: "#E5E7EB", fontSize: "13px" }}>•</span>

                          <span style={{
                            color: "#333",
                            fontSize: "14px",
                            fontWeight: 400,
                            fontFamily: "'Rubik', sans-serif",
                            marginLeft: "10px"
                          }}>
                            {m.date}
                          </span>
                        </div>

                        {/* Interactive Share Icon */}
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {m.sourceLink && (
                            <a
                              href={m.sourceLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#333333", display: "flex", alignItems: "center" }}
                            >
                              <Share2 size={18} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: "#999", textAlign: "center", padding: "60px 0" }}>
                No media coverage recorded.
              </div>
            )}
          </div>
        );
      case "press":
        return (
          <div>
            <BannerImage />
            <Title level={2} style={{ ...headingStyle, marginBottom: "40px" }}>
              Press Releases
            </Title>
            {Array.isArray(industry.press_releases) && industry.press_releases.length > 0 ? (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
                gap: "32px"
              }}>
                {industry.press_releases.map((press, index) => (
                  <div
                    key={index}
                    style={{
                      background: "linear-gradient(135deg, rgba(255, 143, 0, 0.03) 0%, rgba(255, 143, 0, 0.06) 100%)",
                      borderRadius: "20px",
                      padding: "32px",
                      border: "1px solid rgba(255, 143, 0, 0.15)",
                      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.04)",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-10px)";
                      e.currentTarget.style.boxShadow = "0 20px 48px rgba(255, 143, 0, 0.15)";
                      e.currentTarget.style.borderColor = "#FF8F00";
                      e.currentTarget.style.background = "linear-gradient(135deg, rgba(255, 143, 0, 0.08) 0%, rgba(255, 143, 0, 0.12) 100%)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.04)";
                      e.currentTarget.style.borderColor = "rgba(255, 143, 0, 0.15)";
                      e.currentTarget.style.background = "linear-gradient(135deg, rgba(255, 143, 0, 0.03) 0%, rgba(255, 143, 0, 0.06) 100%)";
                    }}
                  >
                    {/* Top Accent Bar */}
                    <div style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "4px",
                      // background: "linear-gradient(90deg, #470172 0%, #FF8F00 100%)"
                    }} />

                    {/* Press Badge */}
                    <div style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "6px 14px",
                      background: "rgba(71, 1, 114, 0.08)",
                      borderRadius: "4px",
                      marginBottom: "20px",
                      alignSelf: "flex-start"
                    }}>
                      <FileTextOutlined style={{ fontSize: "14px", color: "#470172" }} />
                      <span style={{
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "#470172",
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                        fontFamily: "'Rubik', sans-serif"
                      }}>
                        Press Release
                      </span>
                    </div>

                    {/* Title */}
                    <h3 style={{
                      fontSize: "17px",
                      fontWeight: 400,
                      color: "#1a1a1a",
                      lineHeight: "1.4",
                      margin: "0 0 20px 0",
                      fontFamily: "'Rubik', sans-serif",
                      minHeight: "65px",
                      display: "-webkit-box",
                      WebkitLineClamp: "2",
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden"
                    }}>
                      {press.title}
                    </h3>

                    {/* Date Section */}
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      marginBottom: "24px"
                    }}>
                      <CalendarOutlined style={{
                        fontSize: "16px",
                        color: "#FF8F00"
                      }} />
                      <span style={{
                        fontSize: "14px",
                        color: "#666666",
                        fontWeight: 400,
                        fontFamily: "'Rubik', sans-serif"
                      }}>
                        {press.date ? new Date(press.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        }) : "Date not available"}
                      </span>
                    </div>

                    {/* Divider */}
                    <div style={{
                      height: "1px",
                      background: "linear-gradient(90deg, rgba(255, 143, 0, 0.2) 0%, transparent 100%)",
                      margin: "0 0 24px 0"
                    }} />

                    {/* CTA Button */}
                    {press.newsArticleLink && (
                      <a
                        href={press.newsArticleLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          padding: "6px 10px",
                          background: "linear-gradient(135deg, #470172 0%, #5B1A8C 100%)",
                          color: "#ffffff",
                          borderRadius: "6px",
                          textDecoration: "none",
                          fontSize: "13px",
                          fontWeight: 400,
                          fontFamily: "'Rubik', sans-serif",
                          transition: "all 0.3s ease",
                          boxShadow: "0 4px 12px rgba(71, 1, 114, 0.2)",
                          alignSelf: "center",
                          marginTop: "auto",
                          width: "70%"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.03)";
                          e.currentTarget.style.boxShadow = "0 6px 20px rgba(71, 1, 114, 0.35)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow = "0 4px 12px rgba(71, 1, 114, 0.2)";
                        }}
                      >
                        <span>Read More</span>
                        <LinkOutlined style={{ fontSize: "14px" }} />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                padding: "80px 40px",
                textAlign: "center",
                background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
                borderRadius: "20px",
                border: "2px dashed #e0e0e0"
              }}>
                <div style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, rgba(71, 1, 114, 0.1) 0%, rgba(255, 143, 0, 0.1) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 24px"
                }}>
                  <FileTextOutlined style={{ fontSize: "40px", color: "#470172" }} />
                </div>
                <h3 style={{
                  color: "#1a1a1a",
                  fontSize: "20px",
                  fontWeight: 500,
                  margin: "0 0 8px 0",
                  fontFamily: "'Rubik', sans-serif"
                }}>
                  No Press Releases Available
                </h3>
                <p style={{
                  color: "#666666",
                  fontSize: "15px",
                  fontWeight: 400,
                  margin: 0,
                  fontFamily: "'Rubik', sans-serif"
                }}>
                  Press releases will be displayed here once published
                </p>
              </div>
            )}
          </div>
        );

      case "blogs":
        return (
          <div style={{ padding: "40px 0", fontFamily: "'Inter', sans-serif" }}>
            <BannerImage />
            {/* Section Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '40px', pb: '20px' }}>
              <div>
                <Title level={2} style={{ margin: 0, color: "#3C007A", fontSize: "24px", fontWeight: 500, letterSpacing: '-0.5px' }}>
                  Latest Industry Insights
                </Title>
                <Text style={{ color: '#666666', fontSize: '16px' }}>
                  Expert analysis, trends, and strategic perspectives.
                </Text>
              </div>
            </div>

            {industry.blogs && industry.blogs.length > 0 ? (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                gap: "32px"
              }}>
                {industry.blogs.map((blog, index) => (
                  <article
                    key={index}
                    style={{
                      background: "#ffffff",
                      borderRadius: "16px",
                      display: "flex",
                      flexDirection: "column",
                      border: "1px solid #F3F4F6",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "pointer",
                      overflow: "hidden"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-8px)";
                      e.currentTarget.style.boxShadow = "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.05)";
                    }}
                  >
                    {/* Optional: Blog Image Placeholder */}
                    <div style={{ width: '100%', height: '200px', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #F3F4F6' }}>
                      <FileTextOutlined style={{ fontSize: '40px', color: '#E5E7EB' }} />
                    </div>

                    <div style={{ padding: "24px", flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      {/* Metadata Badge */}
                      <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>


                        <span style={{ color: '#333', fontSize: '12px', fontWeight: 400 }}>
                          {blog.date ? new Date(blog.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : "Recently Published"}
                        </span>
                      </div>

                      <Title level={3} style={{
                        margin: "0 0 12px 0",
                        color: "#333",
                        fontSize: "17px",
                        fontWeight: 500,
                        lineHeight: 1.4,
                        minHeight: '56px'
                      }}>
                        {blog.title}
                      </Title>

                      <p style={{
                        fontSize: "15px",
                        color: "#666",
                        lineHeight: "1.6",
                        marginBottom: "20px",
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {blog.content}
                      </p>

                      <div style={{ marginTop: 'auto', paddingTop: '20px', borderTop: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                          <Text style={{
                            fontSize: '14px', fontWeight: 400, color: '#3C007A', background: "rgba(60, 0, 122, 0.08)",
                            padding: "4px 10px",
                            borderRadius: "6px"
                          }}>{blog.author}</Text>
                        </div>
                        <Text style={{ color: '#3C007A', fontWeight: 400, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Read Article <ArrowRightOutlined style={{ fontSize: '12px' }} />
                        </Text>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div style={{
                padding: "100px 0",
                textAlign: "center",
                background: "#F9FAFB",
                borderRadius: "16px",
                border: "1px solid #E5E7EB"
              }}>
                <Empty description={<Text style={{ color: "#9CA3AF", fontSize: "16px" }}>Currently no published articles in this sector.</Text>} />
              </div>
            )}
          </div>
        );

      case "events":
        return (
          <div style={{ padding: "40px 0", fontFamily: "'Inter', sans-serif" }}>
            <BannerImage />
            {/* Section Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '48px' }}>
              {/* <div style={{ width: '4px', height: '32px', background: '#3C007A', borderRadius: '2px' }} /> */}
              <Title level={2} style={{ margin: 0, color: "#3C007A", fontSize: "24px", fontWeight: 500, letterSpacing: '-0.5px' }}>
                Events
              </Title>
            </div>

            {industry.events && industry.events.length > 0 ? (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
                gap: "32px"
              }}>
                {industry.events.map((event, index) => {
                  const eventDate = event.date ? new Date(event.date) : null;
                  const day = eventDate ? eventDate.getDate() : "--";
                  const month = eventDate ? eventDate.toLocaleString('default', { month: 'short' }) : "TBA";

                  return (
                    <div
                      key={index}
                      style={{
                        background: "linear-gradient(135deg, rgba(255, 143, 0, 0.03) 0%, rgba(255, 143, 0, 0.06) 100%)",
                        borderRadius: "6px",
                        padding: "5px",
                        border: "1px solid rgba(255, 143, 0, 0.15)",
                        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05)",
                        minHeight: "280px",
                        transition: "all 0.3s ease",
                        cursor: "pointer"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-8px)";
                        e.currentTarget.style.boxShadow = "0 20px 25px -5px rgba(0, 0, 0, 0.1)";
                        e.currentTarget.style.borderColor = "#E9D5FF";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.05)";
                        e.currentTarget.style.borderColor = "#F3F4F6";
                      }}
                    >
                      <div style={{ padding: "32px" }}>
                        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', marginBottom: '24px' }}>
                          {/* Calendar Badge */}
                          <div style={{
                            flexShrink: 0,
                            width: '60px',
                            height: '64px',
                            background: '#F5F3FF',
                            borderRadius: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #DDD6FE'
                          }}>
                            <span style={{ color: '#3C007A', fontSize: '12px', fontWeight: 500, textTransform: 'uppercase' }}>{month}</span>
                            <span style={{ color: '#333', fontSize: '26px', fontWeight: 700 }}>{day}</span>
                          </div>

                          <div style={{ flexGrow: 1 }}>
                            <h3 style={{
                              fontSize: "20px",
                              fontWeight: "500",
                              color: "#3C007A",
                              margin: "0 0 8px 0",
                              lineHeight: 1.3
                            }}>
                              {event.title}
                            </h3>

                          </div>
                        </div>

                        <p style={{
                          fontSize: "16px",
                          color: "#4B5563",
                          lineHeight: "1.7",
                          marginBottom: "12px",
                          minHeight: "75px"
                        }}>
                          {event.description}
                        </p>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          paddingTop: '20px',
                          borderTop: '1px solid #656868ff'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', color: '#3C007A', fontSize: '16px' }}>
                            <EnvironmentOutlined style={{ marginRight: "6px", color: '#3C007A' }} />
                            {event.location}
                          </div>
                          <div style={{
                            padding: "4px 10px",
                            borderRadius: "6px",
                            background: "rgba(60, 0, 122, 0.08)",
                            color: '#3C007A',

                            fontSize: '16px',
                            fontWeight: 400
                          }}>
                            {event.date}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                padding: "100px 0",
                textAlign: "center",
                background: "#F9FAFB",
                borderRadius: "16px",
                border: "1px dashed #E5E7EB"
              }}>
                <CalendarOutlined style={{ fontSize: "48px", color: "#D1D5DB", marginBottom: "16px" }} />
                <p style={{ color: "#6B7280", fontSize: "16px" }}>No scheduled events at this time.</p>
              </div>
            )}
          </div>
        );

      case "awards":
        return (
          <div>
            <BannerImage />
            <Title level={2} style={{ ...headingStyle }}>
              Awards & Recognition
            </Title>
            {Array.isArray(industry.awards) && industry.awards.length ? (
              <div style={{
                display: "grid",
                // Reduced width by decreasing minmax to 280px
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 24
              }}>
                {industry.awards.map((a, i) => (
                  <div
                    key={i}
                    style={{
                      background: "#ffffff",
                      borderRadius: 20,
                      padding: "32px 24px 24px 24px",
                      position: "relative",
                      border: "1px solid #f1f1f1",
                      boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
                      transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)", // Smoother transition
                      cursor: "pointer",
                      textAlign: "center",
                      marginTop: "50px"
                    }}
                    onMouseEnter={(e) => {
                      const container = e.currentTarget;
                      container.style.transform = "translateY(-10px)";
                      container.style.background = "#3C007A"; // Background change
                      container.style.boxShadow = "0 20px 50px rgba(60, 0, 122, 0.25)";

                      // Target text elements for color inversion
                      const h5 = container.querySelector('h5');
                      const p = container.querySelector('p');
                      const btn = container.querySelector('.cert-link');
                      if (h5) h5.style.color = "#ffffff";
                      if (p) p.style.color = "rgba(255,255,255,0.8)";
                      if (btn) btn.style.color = "#FF8F00"; // Keep amber for contrast
                    }}
                    onMouseLeave={(e) => {
                      const container = e.currentTarget;
                      container.style.transform = "translateY(0)";
                      container.style.background = "#ffffff";
                      container.style.boxShadow = "0 12px 40px rgba(0,0,0,0.08)";

                      const h5 = container.querySelector('h5');
                      const p = container.querySelector('p');
                      const btn = container.querySelector('.cert-link');
                      if (h5) h5.style.color = "#333333";
                      if (p) p.style.color = "#666666";
                      if (btn) btn.style.color = "#3C007A";
                    }}
                  >
                    {/* Achievement Badge Icon */}
                    <div style={{
                      position: "absolute",
                      top: "-35px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "65px",
                      height: "65px",
                      borderRadius: "50%",
                      background: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "4px solid #fff",
                      boxShadow: "0 6px 15px rgba(255, 143, 0, 0.3)",
                      zIndex: 2
                    }}>
                      <img src="/img/trophy.png" alt="Trophy" style={{ width: "40px", height: "40px" }} />
                    </div>

                    {/* Award Content */}
                    <div style={{ marginTop: "15px" }}>
                      <span style={{
                        color: "#FF8F00",
                        fontSize: "12px",
                        fontWeight: 400,
                        textTransform: "uppercase",
                        letterSpacing: "1.2px",
                        display: "inline-block",
                        marginBottom: "10px",
                        background: "rgba(255, 143, 0, 0.1)",
                        padding: "4px 10px",
                        borderRadius: "4px"
                      }}>
                        {a.category || "Excellence"}
                      </span>

                      <h5 style={{
                        fontSize: "17px",
                        color: "#333333",
                        fontWeight: 400,
                        lineHeight: 1.3,
                        margin: "0 0 10px 0",
                        fontFamily: "'Rubik', sans-serif",
                        transition: "color 0.3s ease"
                      }}>
                        {a.awardName || "Award Recognition"}
                      </h5>

                      <p style={{
                        fontSize: "13px",
                        color: "#666666",
                        fontWeight: 400,
                        lineHeight: 1.5,
                        margin: "0 0 18px 0",
                        minHeight: "40px",
                        transition: "color 0.3s ease"
                      }}>
                        {a.organization || "National Recognition Board"}
                      </p>

                      {/* Document Footer */}
                      <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        paddingTop: "14px",
                        borderTop: "2px solid rgba(0,0,0,0.05)"
                      }}>
                        {a.certificate && (
                          <a
                            className="cert-link"
                            href={`${process.env.REACT_APP_API_BASE_URL}/uploads/${a.certificate}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#3C007A",
                              fontSize: "13px",
                              fontWeight: 500,
                              textDecoration: "none",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              transition: "color 0.3s ease"
                            }}
                          >
                            VIEW CERTIFICATE <FileText size={14} />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: "#999", textAlign: "center", padding: "60px 0" }}>
                No awards or recognitions listed for this industry.
              </div>
            )}
          </div>
        );

      case "resources":
        return (
          <div>
            <BannerImage />
            <Title level={2} style={{ ...headingStyle }}>
              Resources & Downloads
            </Title>
            {Array.isArray(industry?.resources) && industry.resources.length > 0 ? (
              <div style={{
                display: "grid",
                // Refined grid for professional spacing
                gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
                gap: 24
              }}>
                {industry.resources.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      background: "#FEF9F2", // Professional cream background
                      borderRadius: 16,
                      padding: "32px 24px",
                      border: "1px solid rgba(0,0,0,0.03)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      position: "relative",
                      transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.04)"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-8px)";
                      e.currentTarget.style.boxShadow = "0 12px 30px rgba(0,0,0,0.1)";
                      e.currentTarget.style.background = "#ffffff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.04)";
                      e.currentTarget.style.background = "#FEF9F2";
                    }}
                  >

                    <div style={{
                      width: 70,
                      height: 70,
                      borderRadius: "50%",
                      background: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 24,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                    }}>
                      <img src="/img/download-file.png" alt="Download" style={{ width: "45px", height: "45px" }} />
                    </div>

                    <div style={{ flex: 1 }}>
                      <h5 style={{
                        fontSize: "19px",
                        color: "#000",
                        fontWeight: 400,
                        lineHeight: 1.3,
                        margin: "0 0 12px 0",
                        fontFamily: "'Rubik', sans-serif"
                      }}>
                        {r.title || "Resource Document"}
                      </h5>

                      <p style={{
                        fontSize: "15px",
                        color: "#666666",
                        fontWeight: 400, // Strictly weight 400
                        lineHeight: 1.6,
                        margin: 0
                      }}>
                        {r.description || "Access official guidelines and technical reference material."}
                      </p>
                    </div>

                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                padding: "80px 0",
                textAlign: "center",
                background: "#fff",
                borderRadius: 16,
                border: "2px dashed #f0f0f0"
              }}>
                <Info size={40} color="#ccc" style={{ marginBottom: 16 }} />
                <div style={{ color: "#999", fontSize: "18px", fontWeight: 400 }}>
                  No technical resources are currently available for this industry.
                </div>
              </div>
            )}
          </div>
        );

      case "surveys":
        return (
          <div style={{ padding: "40px 0", fontFamily: "'Rubik', sans-serif" }}>
            <BannerImage />
            {/* Section Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '40px' }}>
              <Title level={2} style={{
                margin: 0,
                color: "#3C007A",
                fontSize: "24px",
                fontWeight: 400,
                fontFamily: "'Rubik', sans-serif"
              }}>
                Industry Insights & Surveys
              </Title>
            </div>

            {industry.surveys && industry.surveys.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
                {industry.surveys.map((survey, index) => {
                  const isActive = survey.status === "active";

                  return (
                    <div
                      key={index}
                      style={{
                        background: "linear-gradient(135deg, rgba(255, 143, 0, 0.03) 0%, rgba(255, 143, 0, 0.06) 100%)",

                        borderRadius: "16px",
                        padding: "20px",
                        border: "1px solid #E5E7EB",
                        boxShadow: "0 4px 12px rgba(60, 0, 122, 0.05)",
                        transition: "all 0.3s ease",
                        display: "flex",
                        flexDirection: "column",
                        gap: "24px"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = "0 12px 24px rgba(60, 0, 122, 0.12)";
                        e.currentTarget.style.borderColor = "#FF8F00";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(60, 0, 122, 0.05)";
                        e.currentTarget.style.borderColor = "#E5E7EB";
                      }}
                    >
                      {/* Top Row: Title, Status, and Link */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "32px" }}>
                        <div style={{ flexGrow: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "12px" }}>
                            <h3 style={{
                              margin: 0,
                              fontSize: "21px",
                              fontWeight: 400,
                              color: "#3C007A",
                              fontFamily: "'Rubik', sans-serif"
                            }}>
                              {survey.title}
                            </h3>
                            <span style={{
                              padding: "3px 6px",
                              backgroundColor: isActive ? "rgba(60, 0, 122, 0.05)" : "#F3F4F6",
                              color: isActive ? "#3C007A" : "#666666",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: 400,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              border: isActive ? "1px solid #3C007A" : "1px solid #D1D5DB"
                            }}>
                              {survey.status}
                            </span>
                          </div>

                          {/* Meta Info: Dates */}
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "16px",
                            color: "#666666",
                            fontSize: "16px",
                            fontWeight: 400
                          }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <CalendarOutlined style={{ color: "#333" }} />
                              {survey.startDate ? new Date(survey.startDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : "N/A"}
                              {" — "}
                              {survey.endDate ? new Date(survey.endDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : "TBA"}
                            </span>
                          </div>
                        </div>

                        {survey.surveyLink && (
                          <a
                            href={survey.surveyLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: "7px 14px",
                              backgroundColor: isActive ? "#FF8F00" : "#666666",
                              color: "#fff",
                              borderRadius: "8px",
                              textDecoration: "none",
                              fontSize: "16px",
                              fontWeight: 400,
                              transition: "all 0.2s ease",
                              pointerEvents: isActive ? "auto" : "none",
                              boxShadow: isActive ? "0 4px 10px rgba(255, 143, 0, 0.3)" : "none",
                              fontFamily: "'Rubik', sans-serif"
                            }}
                            onMouseEnter={(e) => isActive && (e.currentTarget.style.backgroundColor = "#e07d00")}
                            onMouseLeave={(e) => isActive && (e.currentTarget.style.backgroundColor = "#FF8F00")}
                          >
                            Participate Now
                          </a>
                        )}
                      </div>

                      {/* Content Area */}
                      <div style={{
                        backgroundColor: "rgba(249, 250, 251, 0.5)",
                        padding: "5px",
                        borderRadius: "12px",
                        // borderLeft: "4px solid #3C007A"
                      }}>
                        <p style={{
                          margin: 0,
                          fontSize: "16px",
                          color: "#666",
                          lineHeight: "1.8",
                          fontWeight: 400,
                          fontFamily: "'Rubik', sans-serif"
                        }}>
                          {survey.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                padding: "80px",
                textAlign: "center",
                background: "#F9FAFB",
                borderRadius: "16px",
                border: "2px dashed #D1D5DB"
              }}>
                <Empty description={
                  <span style={{ color: "#666666", fontSize: "18px", fontWeight: 400, fontFamily: "'Rubik', sans-serif" }}>
                    No industry surveys are active at this time.
                  </span>
                } />
              </div>
            )}
          </div>
        );
      case "success":
        return (
          <div style={{ padding: "40px 0", fontFamily: "'Rubik', sans-serif" }}>
            <BannerImage />
            {/* Section Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '48px' }}>
              {/* <div style={{ width: '6px', height: '32px', background: '#FF8F00', borderRadius: '3px' }} /> */}
              <Title level={2} style={{
                margin: 0,
                color: "#3C007A",
                fontSize: "24px",
                fontWeight: 400,
                fontFamily: "'Rubik', sans-serif"
              }}>
                Success Stories & Case Studies
              </Title>
            </div>

            {industry.successMetrics?.caseStudies && industry.successMetrics.caseStudies.length > 0 ? (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
                gap: "32px"
              }}>
                {industry.successMetrics.caseStudies.map((caseStudy, index) => (
                  <div
                    key={index}
                    style={{
                      background: "#ffffff",
                      borderRadius: "16px",
                      border: "1px solid #E5E7EB",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.3s ease"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-8px)"}
                    onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                  >
                    {/* Image Header following image_5877e2.png */}
                    <div style={{ position: 'relative', height: '240px', overflow: 'hidden' }}>
                      <img
                        src={caseStudy.image || "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800"}
                        alt={caseStudy.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>

                    {/* Content Area */}
                    <div style={{ padding: "22px", flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Text style={{
                        color: "#666666",
                        fontSize: "16px",
                        fontWeight: 400,
                        display: "block",
                        marginBottom: "8px"
                      }}>
                        {caseStudy.companyName} • {caseStudy.year}
                      </Text>

                      <h3 style={{
                        fontSize: "20px",
                        fontWeight: 400,
                        color: "#333333",
                        lineHeight: "1.4",
                        marginBottom: "16px",
                        fontFamily: "'Rubik', sans-serif"
                      }}>
                        Case Study: {caseStudy.title}
                      </h3>

                      <p style={{
                        fontSize: "15px",
                        color: "#666666",
                        lineHeight: "1.8",
                        marginBottom: "10px",
                        fontWeight: 400
                      }}>
                        {caseStudy.description}
                      </p>

                      {/* Metrics Row */}
                      {/* <div style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center",
                  marginTop: "auto",
                  paddingTop: "24px",
                  borderTop: "1px solid #666"
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 16px',
                    background: 'rgba(60, 0, 122, 0.05)',
                    borderRadius: '20px'
                  }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3C007A' }} />
                   
                  </div>

                  <a 
                    href="#" 
                    style={{ 
                      color: "#3C007A", 
                      fontSize: "16px", 
                      fontWeight: 400, 
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}
                  >
                    Read More <span style={{ fontSize: "18px" }}>↗</span>
                  </a>
                </div> */}
                    </div>

                    {/* Data Overlay (Stats) */}
                    <div style={{
                      background: "#3C007A",
                      padding: "10px 32px",
                      display: "flex",
                      gap: "32px"
                    }}>
                      <div>
                        <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: "12px", display: "block" }}>Investment</Text>
                        <Text style={{ color: "#FF8F00", fontSize: "16px", fontWeight: 400 }}>{caseStudy.investmentAmount}</Text>
                      </div>
                      <div>
                        <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: "12px", display: "block" }}>Jobs</Text>
                        <Text style={{ color: "#FF8F00", fontSize: "16px", fontWeight: 400 }}>{caseStudy.jobsCreated}</Text>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                padding: "80px",
                textAlign: "center",
                background: "#F9FAFB",
                borderRadius: "16px",
                border: "2px dashed #D1D5DB"
              }}>
                <Empty description={
                  <span style={{ color: "#666666", fontSize: "18px", fontWeight: 400 }}>
                    No success stories or case studies available at this time.
                  </span>
                } />
              </div>
            )}
          </div>
        );

      default:
        return <Empty description={<Text style={baseTextStyle}>Content not available</Text>} />;
    }
  };

  const baseTextStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "18px",
    fontWeight: 400,
    color: "#470172"
  };

  const activeTabMeta = tabs.find((tab) => tab.id === activeTab);

  return (
    <div className="industry-page-wrapper" style={{ fontFamily: "Rubik, sans-serif", backgroundColor: "#fff", marginTop: "60px" }}>
      {/* Breadcrumb */}
      <div className="industry-breadcrumb" style={{
        maxWidth: "1400px",
        margin: "0px auto",
        padding: "0 20px",
        fontSize: "18px",
        color: "#666",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "10px"
      }}>
        <div>
          <span style={{ fontSize: "17px" }}>Industry</span>
          <span style={{ margin: "0 10px" }}>/</span>
          <span style={{ color: "#333", fontWeight: "400", fontSize: "18px" }}>{industry?.sectorName}</span>
        </div>
      </div>

      {/* Main Content with Vertical Tabs */}
      <style>{`
        .industry-mobile-toggler-bar { display: none; }
        .industry-sidebar-backdrop { display: none; }
        .industry-sidebar-close-row { display: none; }

        @media (max-width: 900px) {
          .industry-sidebar-close-row {
            display: flex;
            border-bottom: 1px solid #e0e0e0;
          }
          .industry-mobile-toggler-bar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin: 0 20px 16px;
            padding: 10px 14px;
            background: #fff;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.06);
          }
          .industry-mobile-toggler-active {
            display: flex;
            align-items: center;
            gap: 10px;
            color: #470172;
            font-size: 16px;
            font-weight: 500;
            font-family: 'Rubik', sans-serif;
          }
          .industry-mobile-toggler {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            flex-shrink: 0;
            background: #470172;
            color: #fff;
            border: none;
            border-radius: 8px;
            cursor: pointer;
          }
          .industry-content-row { display: block !important; }
          .industry-sidebar-col {
            position: static !important;
            width: auto !important;
            max-width: 100%;
            box-sizing: border-box;
            margin: 0 20px 16px;
            display: none;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.06);
            overflow: hidden;
          }
          .industry-sidebar-col.industry-sidebar-open {
            display: block;
          }
          .industry-sidebar-col > div {
            background: #fff !important;
          }
          .industry-main-col {
            padding: 0 20px !important;
          }
          .industry-banner-wrap {
            padding: 0 !important;
          }
          .industry-featured-heading {
            flex-direction: column !important;
            align-items: center !important;
            text-align: center;
            gap: 16px;
          }
          .industry-featured-heading-title {
            font-size: 22px !important;
            text-align: center;
          }
          .industry-featured-heading-controls {
            justify-content: center !important;
          }
          .industry-page-wrapper {
            margin-top: 12px !important;
          }
          .industry-breadcrumb {
            padding: 0 !important;
            justify-content: center !important;
            margin: 8px auto 20px !important;
          }
          .industry-main-section {
            padding: 0 !important;
          }
        }
      `}</style>
      <section className="industry-main-section" style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "0 20px",
        marginBottom: "60px"
      }}>
        <div
          className="industry-mobile-toggler-bar"
          style={mobileSidebarOpen ? { display: "none" } : undefined}
        >
          <span className="industry-mobile-toggler-active">
            <span style={{ fontSize: "18px", display: "flex" }}>{activeTabMeta?.icon}</span>
            {activeTabMeta?.label}
          </span>
          <button
            type="button"
            className="industry-mobile-toggler"
            onClick={() => setMobileSidebarOpen(true)}
            aria-label="Open sections menu"
          >
            <Menu size={22} />
          </button>
        </div>

        <div
          className="industry-sidebar-backdrop"
          onClick={() => setMobileSidebarOpen(false)}
        />

        <div className="industry-content-row" style={{ display: "flex", gap: "0" }}>
          {/* Vertical Sidebar Tabs */}
          <div className={`industry-sidebar-col ${mobileSidebarOpen ? "industry-sidebar-open" : ""}`} style={{ width: "260px", flexShrink: 0, backgroundColor: "#f8f9fa" }}>
            <div className="industry-sidebar-close-row" style={{ display: "flex", justifyContent: "flex-end", padding: "10px 14px" }}>
              <button
                type="button"
                className="industry-mobile-toggler"
                style={{ margin: 0 }}
                onClick={() => setMobileSidebarOpen(false)}
                aria-label="Close sections menu"
              >
                <X size={18} />
              </button>
            </div>
            {tabs.map((tab) => (
              <div
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setMobileSidebarOpen(false); }}
                style={{
                  padding: "18px 24px",
                  cursor: "pointer",
                  backgroundColor: activeTab === tab.id ? "#f0e5f5" : "transparent",
                  borderLeft: activeTab === tab.id ? "4px solid #470172" : "4px solid transparent",
                  transition: "all 0.3s ease",
                  fontFamily: "Rubik, sans-serif",
                  fontSize: "16px",
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  color: activeTab === tab.id ? "#470172" : "#666",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px"
                }}
              >
                <span style={{ fontSize: "18px", flexShrink: 0, display: "flex" }}>{tab.icon}</span>
                <span style={{ lineHeight: 1.3 }}>{tab.label}</span>
              </div>
            ))}
          </div>

          {/* Tab Content */}
          <div className="industry-main-col" style={{ flex: 1, padding: "15px 30px" }}>
            {renderTabContent()}
          </div>
        </div>
      </section>

      {/* Featured Industries Section */}
      <section style={{
        backgroundColor: "#f8f9fa",
        padding: "50px 0",
        fontFamily: "'Rubik', sans-serif"
      }}>
        <div style={{
          maxWidth: "1440px",
          margin: "0 auto",
          padding: "0 20px"
        }}>
          {/* Section Heading with Slider Controls */}
          <div className="industry-featured-heading" style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "45px"
          }}>
            <h2 className="industry-featured-heading-title" style={{
              fontSize: "28px",
              fontWeight: "500",
              margin: 0,
              color: "#333333",
              fontFamily: "'Rubik', sans-serif"
            }}>
              Featured Industries in {industry?.sectorName}
            </h2>

            {/* Slider Controls - Only show if companies exist */}
            {companies.length > 0 && (
              <div className="industry-featured-heading-controls" style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => {
                    const container = document.getElementById('industry-companies-container');
                    if (container) {
                      container.scrollBy({ left: -400, behavior: 'smooth' });
                    }
                  }}
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    border: "2px solid #E5E7EB",
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#470172";
                    e.currentTarget.style.background = "#F3E8FF";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#E5E7EB";
                    e.currentTarget.style.background = "#fff";
                  }}
                >
                  <ChevronLeft size={20} color="#470172" />
                </button>

                <button
                  onClick={() => {
                    const container = document.getElementById('industry-companies-container');
                    if (container) {
                      container.scrollBy({ left: 400, behavior: 'smooth' });
                    }
                  }}
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    border: "2px solid #E5E7EB",
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#470172";
                    e.currentTarget.style.background = "#F3E8FF";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#E5E7EB";
                    e.currentTarget.style.background = "#fff";
                  }}
                >
                  <ChevronRight size={20} color="#470172" />
                </button>
              </div>
            )}
          </div>

          {/* Loading State */}
          {companiesLoading ? (
            <div style={{
              textAlign: "center",
              padding: "80px 20px",
              backgroundColor: "white",
              borderRadius: "20px",
              border: "2px dashed #E5E7EB"
            }}>
              <div style={{
                width: "60px",
                height: "60px",
                border: "4px solid #f3f4f6",
                borderTopColor: "#470172",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px"
              }} />
              <p style={{
                color: "#666666",
                fontSize: "17px",
                fontWeight: 400,
                fontFamily: "'Rubik', sans-serif"
              }}>
                Loading companies...
              </p>
            </div>
          ) : companiesError ? (
            /* Error State */
            <div style={{
              textAlign: "center",
              padding: "80px 20px",
              backgroundColor: "white",
              borderRadius: "20px",
              border: "2px dashed #E5E7EB"
            }}>
              <div style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                background: "#FEE2E2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px"
              }}>
                <AlertCircle size={40} color="#DC2626" />
              </div>
              <h3 style={{
                color: "#333333",
                fontSize: "24px",
                fontWeight: 600,
                margin: "0 0 12px 0",
                fontFamily: "'Rubik', sans-serif"
              }}>
                Error Loading Companies
              </h3>
              <p style={{
                color: "#666666",
                fontSize: "16px",
                margin: 0,
                fontFamily: "'Rubik', sans-serif"
              }}>
                {companiesError}
              </p>
            </div>
          ) : companies.length === 0 ? (
            /* Empty State - No Companies */
            <div style={{
              textAlign: "center",
              padding: "80px 20px",
              backgroundColor: "white",
              borderRadius: "20px",
              border: "2px dashed #E5E7EB"
            }}>
              <div style={{
                width: "100px",
                height: "100px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #F3E8FF 0%, #FFF5E6 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px auto"
              }}>
                <Building2 size={50} color="#470172" />
              </div>
              <h3 style={{
                color: "#333333",
                fontSize: "24px",
                fontWeight: 600,
                margin: "0 0 12px 0",
                fontFamily: "'Rubik', sans-serif"
              }}>
                No Companies Found
              </h3>
              <p style={{
                color: "#666666",
                fontSize: "16px",
                margin: 0,
                fontFamily: "'Rubik', sans-serif"
              }}>
                Companies in {industry?.sectorName} sector will appear here once registered
              </p>
            </div>
          ) : (
            /* Companies Horizontal Scroll */
            <div
              id="industry-companies-container"
              style={{
                display: "flex",
                gap: "24px",
                overflowX: "auto",
                overflowY: "hidden",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
                paddingBottom: "20px"
              }}
            >
              <style>{`
                #industry-companies-container::-webkit-scrollbar {
                  display: none;
                }
              `}</style>

              {companies.map((company, index) => (
                <div key={company._id || index} style={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  overflow: "visible",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  border: "1px solid #eee",
                  minWidth: "330px",
                  maxWidth: "330px",
                  flexShrink: 0
                }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-12px)";
                    e.currentTarget.style.boxShadow = "0 20px 50px rgba(71, 1, 114, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.08)";
                    e.currentTarget.style.borderColor = "#eee";
                  }}
                >
                  {/* Banner Image */}
                  <div style={{
                    height: "220px",
                    overflow: "hidden",
                    borderRadius: "12px 12px 0 0",
                    backgroundColor: "#f3f4f6"
                  }}>
                    <img
                      alt={company.companyName || "Company Banner"}
                      src={company.banner
                        ? `${process.env.REACT_APP_API_BASE_URL}/${company.banner.replace(/\\/g, '/').replace(/^\/+/, '')}`
                        : "/img/company/bg.jpg"
                      }
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                      }}
                      onError={(e) => {
                        e.target.src = "/img/company/bg.jpg";
                      }}
                    />
                  </div>

                  {/* Floating Logo */}
                  <div style={{
                    width: "100px",
                    height: "100px",
                    backgroundColor: "white",
                    borderRadius: "50%",
                    position: "absolute",
                    top: "160px",
                    left: "20px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "3px solid white"
                  }}>
                    <img
                      alt={company.companyName || "Company Logo"}
                      src={company.logo
                        ? `${process.env.REACT_APP_API_BASE_URL}/${company.logo.replace(/\\/g, '/').replace(/^\/+/, '')}`
                        : "/img/company/logo-placeholder.png"
                      }
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "50%"
                      }}
                      onError={(e) => {
                        e.target.src = "/img/company/logo-placeholder.png";
                      }}
                    />
                  </div>

                  {/* Content */}
                  <div style={{ padding: "40px 20px 20px 20px" }}>
                    <h4 style={{
                      fontSize: "21px",
                      fontWeight: 400,
                      marginBottom: "6px",
                      color: "#470172",
                      fontFamily: "'Rubik', sans-serif"
                    }}>
                      {company.companyName}
                    </h4>

                    <p style={{
                      fontSize: "16px",
                      color: "#666666",
                      marginBottom: "20px",
                      fontWeight: "400"
                    }}>
                      {company.address?.city}
                    </p>

                    {/* Information List */}
                    <div style={{ paddingTop: "10px", marginBottom: "25px" }}>
                      <div style={{ display: "flex", marginBottom: "12px", alignItems: "center" }}>
                        <span style={{ minWidth: "100px", color: "#333333", fontSize: "16px", marginRight: "10px" }}>Industry</span>
                        <span style={{ color: "#666666", fontSize: "15px" }}>{company.industry || "N/A"}</span>
                      </div>

                      <div style={{ display: "flex", marginBottom: "12px", alignItems: "center" }}>
                        <span style={{ minWidth: "100px", color: "#333333", fontSize: "16px", marginRight: "10px" }}>Phone No.</span>
                        <span style={{ color: "#666666", fontSize: "15px" }}>{company.phone || "N/A"}</span>
                      </div>

                      <div style={{ display: "flex", alignItems: "center" }}>
                        <span style={{ minWidth: "100px", color: "#333333", fontSize: "16px", marginRight: "10px" }}>Email</span>
                        <span style={{ color: "#666666", fontSize: "15px", wordBreak: "break-word" }}>{company.email || "N/A"}</span>
                      </div>
                    </div>

                    {/* Divider */}
                    <div style={{
                      height: "1px",
                      background: "linear-gradient(90deg, transparent 0%, #e5e7eb 50%, transparent 100%)",
                      marginBottom: "20px"
                    }} />

                    {/* Actions */}
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}>
                      <div style={{ display: "flex", gap: "7px" }}>
                        <a
                          href={`tel:${company.phone}`}
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: "rgba(71, 1, 114, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.3s ease",
                            textDecoration: "none"
                          }}
                        >
                          <Phone size={20} color="#470172" />
                        </a>

                        <a
                          href={`mailto:${company.email}`}
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: "rgba(71, 1, 114, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.3s ease",
                            textDecoration: "none"
                          }}
                        >
                          <Mail size={20} color="#470172" />
                        </a>

                        <a
                          href={company.website}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: "rgba(71, 1, 114, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.3s ease",
                            textDecoration: "none"
                          }}
                        >
                          <LinkIcon size={20} color="#470172" />
                        </a>
                      </div>

                      <a
                        href={`/company/${company._id}`}
                        style={{
                          padding: "6px 28px",
                          border: "1px solid #470172",
                          borderRadius: "8px",
                          color: "#470172",
                          textDecoration: "none",
                          fontSize: "14px",
                          fontWeight: "400",
                          transition: "all 0.3s ease"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#470172";
                          e.currentTarget.style.color = "white";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.color = "#470172";
                        }}
                      >
                        Details
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Add keyframes for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default IndustryPage;